import { useEffect, useState, type ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { ApiError } from '../api/client';
import { mikrotikApi, settingsApi } from '../api/endpoints';
import { PageHeader } from '../components/PageHeader';
import { useToast } from '../app/ToastProvider';

const emptyPrefixes = { client_prefix: '', invoice_prefix: '', payment_prefix: '' };
const validPrefix = /^[A-Za-z0-9]+(?:[-_][A-Za-z0-9]+)*$/;

export function SettingsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [prefixes, setPrefixes] = useState(emptyPrefixes);
  const settings = useQuery({ queryKey: ['settings'], queryFn: settingsApi.get });

  useEffect(() => {
    if (settings.data) setPrefixes({
      client_prefix: settings.data.client_prefix ?? 'BFC',
      invoice_prefix: settings.data.invoice_prefix ?? 'INV',
      payment_prefix: settings.data.payment_prefix ?? 'PAY',
    });
  }, [settings.data]);

  const mutation = useMutation({
    mutationFn: () => settingsApi.updatePrefixes({
      client_prefix: prefixes.client_prefix.trim(), invoice_prefix: prefixes.invoice_prefix.trim(), payment_prefix: prefixes.payment_prefix.trim(),
    }),
    onSuccess: async (result) => { toast.success(result.message); await queryClient.invalidateQueries({ queryKey: ['settings'] }); },
  });

  const mikrotikSync = useMutation({
    mutationFn: mikrotikApi.syncCrm,
    onSuccess: async ({ message }) => {
      toast.success(message);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['clients'] }),
        queryClient.invalidateQueries({ queryKey: ['subscriptions'] }),
        queryClient.invalidateQueries({ queryKey: ['service-plans'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
  });

  const set = (key: keyof typeof prefixes) => (event: ChangeEvent<HTMLInputElement>) => setPrefixes((current) => ({ ...current, [key]: event.target.value }));
  const invalid = Object.values(prefixes).some((value) => !validPrefix.test(value.trim()));

  return <>
    <PageHeader title="Settings" description="Configure identifiers used for future CRM records." />
    {settings.isError ? <Alert severity="error" sx={{ mb: 2 }}>Settings could not be loaded.</Alert> : null}
    <Stack spacing={3} sx={{ maxWidth: 680 }}>
    <Card><CardContent><Stack spacing={2}>
      <Alert severity="info">Changing a prefix affects new records only. Existing numbers will not be renamed.</Alert>
      <TextField label="Client prefix" value={prefixes.client_prefix} onChange={set('client_prefix')} helperText="Example: ABC creates ABC000001" inputProps={{ maxLength: 20 }} required />
      <TextField label="Invoice prefix" value={prefixes.invoice_prefix} onChange={set('invoice_prefix')} helperText="Example: BILL creates BILL-2026-000001" inputProps={{ maxLength: 20 }} required />
      <TextField label="Payment prefix" value={prefixes.payment_prefix} onChange={set('payment_prefix')} helperText="Example: RCPT creates RCPT-2026-000001" inputProps={{ maxLength: 20 }} required />
      <Button variant="contained" startIcon={<SaveOutlinedIcon />} disabled={settings.isLoading || mutation.isPending || invalid} onClick={() => mutation.mutate()}>Save prefixes</Button>
    </Stack></CardContent></Card>
    <Card><CardContent><Stack spacing={2}>
      <Box>
        <Typography variant="h6" component="h2">MikroTik synchronization</Typography>
        <Typography variant="body2" color="text.secondary">Pull profiles and PPPoE secrets from the router, then push CRM-managed plans and subscribers back to MikroTik.</Typography>
      </Box>
      <Alert severity="info">CRM records remain saved while MikroTik is offline. Run this synchronization after the connection returns to create or update deferred profiles and PPPoE secrets.</Alert>
      {mikrotikSync.isError ? <Alert severity="error">{mikrotikSync.error instanceof ApiError ? mikrotikSync.error.message : 'MikroTik data could not be synchronized.'}</Alert> : null}
      {mikrotikSync.data ? <Alert severity="success">
        Created {mikrotikSync.data.data.clientsCreated} clients, {mikrotikSync.data.data.subscriptionsCreated} subscriptions, and {mikrotikSync.data.data.servicePlansCreated} service plans. Updated {mikrotikSync.data.data.subscriptionsUpdated} subscriptions and {mikrotikSync.data.data.servicePlansUpdated} service plans. Pushed {mikrotikSync.data.data.profilesPushed} profiles and {mikrotikSync.data.data.secretsPushed} secrets to MikroTik. {mikrotikSync.data.data.pushFailures > 0 ? `${mikrotikSync.data.data.pushFailures} push item(s) failed.` : null}
      </Alert> : null}
      <Button variant="contained" startIcon={<SyncOutlinedIcon />} disabled={mikrotikSync.isPending} onClick={() => mikrotikSync.mutate()}>
        {mikrotikSync.isPending ? 'Synchronizing…' : 'Synchronize CRM and MikroTik'}
      </Button>
    </Stack></CardContent></Card>
    </Stack>
  </>;
}
